// Session reader — fetch /me sa cookie auth (Domain=.grozd.me)
//
// In-memory TTL cache (30s) — sprečava redundantne /me pozive pri navigaciji.
// Velibor (2026-05-02): bez ovoga svaki sidebar klik radi novi fetch,
// admin panel se osjeća sporo. 30s je dovoljno za navigaciju, dovoljno kratko
// da logout/2fa-success budu osjećajno trenutni (uz invalidateMe).
//
// Cross-tab sinhronizacija (2026-05-02):
//  - storage event "grozd:logout" → invalidate + redirect na auth login
//    (osim ako smo već na auth.* domeni, da ne radimo redirect petlju)
//  - storage event "grozd:login" → invalidate (NE redirect — drugi tab
//    prirodno fetcha svježi /me kad mu zatreba)
//  - window.focus → SMART invalidate (samo ako je cache vec istekao + 60s
//    grace), da ne forsiramo /me na svakom focus-u

import type { AuthUser } from "./types";

const API_URL =
  (typeof window !== "undefined"
    ? (window as any).__GROZD_API_URL__
    : process.env.NEXT_PUBLIC_API_URL) || (typeof window !== "undefined" && window.location.hostname.endsWith(".dev.grozd.me") ? "https://api.dev.grozd.me" : "https://api.grozd.me");

const TTL_MS = 30_000;
const FOCUS_GRACE_MS = 60_000;

interface CacheEntry {
  value: AuthUser | null;
  expiresAt: number;
}

let cached: CacheEntry | null = null;
// Deduplikacija paralelnih in-flight zahtjeva — ako 5 komponenti istovremeno
// zatraži fetchMe na cold cache-u, samo jedan stvarni fetch ide.
let inflight: Promise<AuthUser | null> | null = null;

/**
 * Fetch /me — ako je TTL cache validan, vrati iz cache-a; inače stvarni HTTP poziv.
 *
 * Cache pravila:
 *  - Pozitivan rezultat (user) keširan 30s.
 *  - Negativan rezultat (null) NIJE keširan — sledeći poziv probaj ponovo
 *    (mreža mogla pasti privremeno, ne želimo zaglaviti app u "logged out" state).
 *  - Greške nikad ne keširaju.
 */
export async function fetchMe(): Promise<AuthUser | null> {
  const now = Date.now();
  if (cached && cached.expiresAt > now) {
    return cached.value;
  }
  if (inflight) {
    return inflight;
  }

  inflight = (async () => {
    try {
      const res = await fetch(`${API_URL}/api/v3/auth/me`, {
        credentials: "include",
        headers: { Accept: "application/json" },
      });
      if (!res.ok) {
        cached = null; // ne keširaj greške/401
        return null;
      }
      const user = (await res.json()) as AuthUser;
      cached = { value: user, expiresAt: Date.now() + TTL_MS };
      return user;
    } catch {
      cached = null;
      return null;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

/**
 * Invalidira cache — pozovi nakon login/logout/2FA verify da sledeći fetchMe
 * povuče svježe podatke umjesto starog cache-a.
 */
export function invalidateMe(): void {
  cached = null;
  inflight = null;
}

export async function getCurrentUser(): Promise<AuthUser | null> {
  return fetchMe();
}

export async function isAuthenticated(): Promise<boolean> {
  return (await getCurrentUser()) !== null;
}

// ─── Cross-tab + window focus listeneri ──────────────────────────────────────
// Inicijalizujemo se SAMO u browseru i SAMO jednom (top-level u modulu —
// ESM cache osigurava single execution). SSR (Next.js server) preskače.
//
// Guarded sa __grozd_session_listeners_attached__ za slučaj da neki bundler
// duplicira modul (npr. dev/prod chunk granica).

declare global {
  interface Window {
    __grozd_session_listeners_attached__?: boolean;
  }
}

if (
  typeof window !== "undefined" &&
  !window.__grozd_session_listeners_attached__
) {
  window.__grozd_session_listeners_attached__ = true;

  // Cross-tab logout broadcast — drugi tab je odjavio, mi pratimo.
  window.addEventListener("storage", (e: StorageEvent) => {
    if (e.key === "grozd:logout") {
      invalidateMe();
      // Ne radimo redirect ako smo već na auth domeni — auth.grozd.me sam
      // upravlja svojim flow-om (login forma, 2FA, security-check).
      const host = window.location.host;
      if (!host.startsWith("auth.")) {
        const isDev = window.location.hostname.endsWith(".dev.grozd.me");
        window.location.href = isDev
          ? "https://auth.dev.grozd.me/login"
          : "https://auth.grozd.me/login";
      }
    } else if (e.key === "grozd:login") {
      // Drugi tab je upravo loginovan — invalidate cache da bi naš sledeći
      // /me fetch dobio novi user/tenant. NE radimo redirect.
      invalidateMe();
    }
  });

  // Smart focus invalidation — kad korisnik se vrati na tab, samo invalidate
  // ako je cache već istekao + grace perioda (60s). Sprječava /me storm na
  // svaki Alt+Tab.
  window.addEventListener("focus", () => {
    if (!cached) return;
    const now = Date.now();
    if (cached.expiresAt < now - FOCUS_GRACE_MS) {
      invalidateMe();
    }
  });
}
